import BarChartIcon from '@mui/icons-material/BarChart';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { WhenLoaded } from '@openx/components-core/lib/Loader/WhenLoaded';
import type { AudienceSegmentSetMetrics } from '@openx/types/gqlTypes';
import debounce from 'lodash/debounce';
import isEqual from 'lodash/isEqual';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDealPrompt } from '../../../hooks/useDealPrompt';
import { useDealPromptAudienceFiltersConfig } from '../../../hooks/useDealPromptAudienceFiltersConfig';
import { useDealPromptDomainsContext } from '../../../utils/context/DealPromptDomainsContext';
import type { AudienceSegmentSetWithSegments } from '../../../utils/getTilesData';
import { createNormalizedDefinitionFromSegmentSetAndSelection } from '../../../utils/normalizedDefinitionFromAudienceItems';
import { getAudienceMetricsData } from '../utils';
import { AudienceAppliedFiltersSummary } from './AudienceAppliedFiltersSummary';
import { DeviceDistribution } from './DeviceDistribution';
import { GatheredMetrics } from './GatheredMetrics';
import { getAudienceAppliedFiltersDisplay } from './getAudienceAppliedFiltersDisplay';
import { SimpleMetric } from './SimpleMetric';

const GET_NEW_METRICS_DEBOUNCE_MS = 500;
const MIN_HEIGHT_OF_METRIC_SECTION = '475px';

type DealPromptAudienceDetailsMetricSectionProps = {
  item: AudienceSegmentSetWithSegments;
};

export const DealPromptAudienceDetailsMetricSection = ({ item }: DealPromptAudienceDetailsMetricSectionProps) => {
  const { t } = useTranslation();

  const {
    state: { selectedAudienceItems, selectedTile, lastSubmittedFilterCriteria },
  } = useDealPrompt();

  const appliedFilters = lastSubmittedFilterCriteria?.filters;
  const { categories, subCategories, accountOptions, locationOptions } =
    useDealPromptAudienceFiltersConfig(appliedFilters);
  const appliedFiltersDisplay = useMemo(
    () =>
      getAudienceAppliedFiltersDisplay(
        appliedFilters,
        {
          stateOptions: locationOptions?.stateSegments ?? [],
          dmaOptions: locationOptions?.dmaSegments ?? [],
          congressionalDistrictOptions: locationOptions?.congressionalSegments ?? [],
          categoryOptions: categories,
          subCategoryOptions: subCategories,
          accountOptions,
        },
        t
      ),
    [appliedFilters, locationOptions, categories, subCategories, accountOptions, t]
  );

  const [metrics, setMetrics] = useState<AudienceSegmentSetMetrics>(item.metrics);
  const previousSelectedAudienceItems = useRef<string[]>(selectedAudienceItems.map(item => item.id));
  const { useDealPromptAudienceCalculateOrf, audienceSegments } = useDealPromptDomainsContext();
  const { calculateOrf, loading } = useDealPromptAudienceCalculateOrf();

  const segmentIds = selectedAudienceItems.map(item => item.id);

  const debouncedGetNewMetrics = useMemo(
    () =>
      debounce(async (ids: string[]) => {
        const scoreBySegmentId = new Map(item.matchedSegments.map(({ id, score }) => [id, score]));

        const orf = await calculateOrf({
          segmentIds: ids,
          normalizedDefinition: createNormalizedDefinitionFromSegmentSetAndSelection(item, selectedAudienceItems),
          // Send metrics for every segment from the search response, not just the
          // selectable ones in selectedAudienceItems. Non-selectable categories
          // (location, demographic_*, lookalike) are never shown as table rows but
          // are still part of the normalized definition; the backend picks whichever
          // it needs.
          segmentMetrics: (audienceSegments ?? []).map(segment => ({
            id: segment.id,
            activeIPs: segment.activeIPs,
            avgCPM: segment.avgCPM,
            clickThroughRate: segment.clickThroughRate,
            cookies: segment.cookies,
            costPerUniqueReach: segment.costPerUniqueReach,
            devicesDesktop: segment.devicesDesktop,
            devicesMobile: segment.devicesMobile,
            devicesTV: segment.devicesTV,
            maxCPM: segment.maxCPM,
            minCPM: segment.minCPM,
            reach: segment.reach,
            requests: segment.requests,
            score: scoreBySegmentId.get(segment.id),
            videoCompletions: segment.videoCompletions,
            videoStarts: segment.videoStarts,
            weightedBidCpm: segment.weightedBidCpm,
            weightedClicks: segment.weightedClicks,
            weightedImpressionsWithClickMacro: segment.weightedImpressionsWithClickMacro,
          })),
        });

        setMetrics(orf ?? item.metrics);
      }, GET_NEW_METRICS_DEBOUNCE_MS),
    [calculateOrf, item, selectedAudienceItems, audienceSegments]
  );

  useEffect(() => {
    if (isEqual(previousSelectedAudienceItems.current, segmentIds)) {
      return;
    }
    previousSelectedAudienceItems.current = segmentIds;
    debouncedGetNewMetrics(segmentIds);

    return () => debouncedGetNewMetrics.cancel();
  }, [segmentIds, debouncedGetNewMetrics]);

  const { simpleCardLabels, simpleCardValues, gatheredMetricsColumns, deviceDistributionItems } =
    getAudienceMetricsData(metrics, t);

  return (
    <Box
      data-test="audience-details-metrics-section"
      sx={{
        borderBottom: '1px solid',
        borderColor: 'divider',
      }}
    >
      <Stack
        direction="row"
        spacing={2}
        sx={{
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          borderBottom: '1px solid',
          borderColor: 'divider',
          p: 4,
        }}
      >
        <Stack spacing={0.5} sx={{ minWidth: 0, flex: 1 }}>
          {selectedTile?.title && (
            <Typography
              data-test="audience-details-set-name"
              variant="h1"
              sx={{
                fontWeight: 'fontWeightBold',
              }}
            >
              {t('{title} Set', { title: selectedTile.title })}
            </Typography>
          )}

          <AudienceAppliedFiltersSummary appliedFilters={appliedFiltersDisplay} />
        </Stack>

        <Stack
          data-test="audience-details-monthly-data"
          direction="row"
          spacing={1}
          sx={{
            alignItems: 'center',
            borderRadius: '50px',
            px: 1.5,
            py: 0.5,
            m: 4,
            backgroundColor: 'background.boxDarkest',
            color: 'primary.contrastText',
            flexShrink: 0,
          }}
        >
          <BarChartIcon />

          <Typography variant="body2Bold">{t('Monthly Data')}</Typography>
        </Stack>
      </Stack>
      {/* This is the minimum height of the metric section. Its necessary to prevent this section from glitching while loader appears. */}
      <Box
        sx={{
          minHeight: MIN_HEIGHT_OF_METRIC_SECTION,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          p: 4,
        }}
      >
        <WhenLoaded loading={loading}>
          <Grid
            container
            spacing={4}
            sx={{
              mb: 4,
              display: 'grid',
              gridTemplateColumns: 'repeat(3, 1fr)',
              width: '100%',
            }}
          >
            {Object.entries(simpleCardLabels).map(([key, value]) => (
              <SimpleMetric
                key={key}
                title={value}
                value={simpleCardValues[key]}
                data-test={`audience-details-metric-${key}`}
              />
            ))}

            <Grid
              data-test="audience-details-gathered-metrics"
              sx={{
                gridColumn: 'span 2',
              }}
            >
              <GatheredMetrics columns={gatheredMetricsColumns} />
            </Grid>

            <DeviceDistribution items={deviceDistributionItems} />
          </Grid>
        </WhenLoaded>
      </Box>
    </Box>
  );
};
